import React, { useEffect, useMemo, useState } from "react";
import { useChatMessageStore } from "./store";
import { Skeleton } from "antd";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import httpClient from "../../api/module/httpClient";
import { useInView } from "react-intersection-observer";

const useCohortStudents = ({ pageSize = 25, userProfile }) => {
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: ["COHORT_STUDENTS", userProfile?.id],
      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await httpClient.get(
          `private/certificate/certificate-users/list`,
          {
            params: {
              page: pageParam,
              num: pageSize,
            },
          },
        );
        return data;
      },
      getNextPageParam: (lastPage) => {
        // If the server indicates there are more pages, return the next page number
        return lastPage.pagination.hasMore ? lastPage.pagination.currentPage + 1 : undefined;
      },
      // Keep previous data while fetching next page
      keepPreviousData: true,
    });

  // Flatten all pages of data into a single array
  // Preserve order from server
  const cohortStudents = useMemo(() => {
    if (!data?.pages) return [];

    const seen = new Set();
    return data.pages.flatMap(page =>
      page.cohortStudents.filter(student => {
        if (seen.has(student.UserId)) return false;
        seen.add(student.UserId);
        return true;
      })
    );
  }, [data]);

  // Helper function to load more data
  const loadMore = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  return {
    cohortStudents,
    isLoading,
    isFetching,
    hasNextPage,
    loadMore,
  };
};

export default function CohortStudents({ userProfile, setText, setIsSearching, setUsers, updateSeenMutation }) {
  const chatMessageStore = useChatMessageStore();

  const { cohortStudents, isLoading, isFetching, hasNextPage, loadMore } = useCohortStudents({
    userProfile,
    pageSize: 25,
    updateSeenMutation
  });

  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: "150px",
    // skip: isFetching, // Skip observation while fetching
    // delay: 100 // Delay trigger by 100ms
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetching) {
      loadMore();
    }
  }, [inView, hasNextPage, isFetching, loadMore]);

  return (
    <>
      {isLoading && (
        <div style={{ padding: "12px 16px" }}>
          {new Array(25).fill(null).map(() => <Skeleton avatar paragraph={{ rows: 1 }} />)}
        </div>
      )}
      {(cohortStudents || []).map(item => {
        const user = item;

        if (userProfile.id === user.UserId) return null;

        const name = `${item.User.firstName} ${item.User.lastName}`;

        const isOnline = chatMessageStore.onlineUsers.some(onlineUser => onlineUser.userId === user.UserId);

        return (
          <div
            key={`searchchatmessage-${user.id}`}
            style={{
              padding: "0 16px",
              paddingTop: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "1rem",
                cursor: "pointer",
                borderBottom: "1px solid rgba(219, 219, 219)",
                paddingBottom: "10px",
              }}
              onClick={(event) => {
                event.stopPropagation();

                updateSeenMutation.mutate({ receiverId: user.User.id });

                chatMessageStore.setChatMessageWindows({
                  img: user.User.img,
                  name: name,
                  id: user.User.id,
                  titleProfessions: user.User.titleProfessions,
                  type: "individual",
                });

                // // go back to last chat messages
                setText("");
                setIsSearching(false);
                setUsers([]);
              }}
            >
              <div style={{ position: "relative", display: "inline-block", height: "100%" }}>
                <img
                  src={user.User.img} alt={name}
                  style={{
                    borderRadius: "9999px",
                    width: "3rem",
                    height: "3rem",
                    maxWidth: "unset",
                  }}
                />
                {isOnline && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "0",
                      right: "0",
                      width: "0.8rem",
                      height: "0.8rem",
                      backgroundColor: "green",
                      borderRadius: "50%",
                      border: "2px solid white", // Add a white border for better visibility
                    }}
                  />
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>{name}</span>
                </div>
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "200px",
                  }}
                >
                    {user.User.titleProfessions}
                  </span>
              </div>
            </div>
          </div>
        );
      })}
      {(hasNextPage && !isFetching) && (
        <div ref={ref} style={{ visibility: "hidden"}}>.</div>
      )}
    </>
  );
}